import React, { useMemo, useCallback } from 'react';
import { PageTemplate } from '../../layout/PageTemplate/PageTemplate';

import './Exercises.scss';

import { Button } from 'primereact/button';
import { ServerResponsiveTable } from '../../components/ServerResponsiveTable/ServerResponsiveTable';
import { TableColumn, TableAction } from '../../types/responsiveTable';
import { Exercise, EXERCISE_TYPE } from '../../types/Fisiofit/exercise';
import { useClient } from '../../hooks/useClient';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { PrimeIcons } from 'primereact/api';
import { ExercisePanel } from '../../components/ExercisePanel/ExercisePanel';
import { useResourcePanel } from '../../hooks/useResourcePanel';
import { showDeleteDialog } from '../../utils/dialogUtils';
import { capitalize } from '../../utils/textUtils';
import { ALL_FEATURES } from '../../config/features';
import { AllowedFeatures } from '../../types/features';
import { hasFeaturesPermission } from '../../utils/featureUtils';
import { useAuthContext } from '../../context/AuthContext';
import { ExportButton } from '../../components/ExportButton/ExportButton';
import { mutate } from 'swr';

export const getExerciseDeleteConfirmationMessage = (exercise: Exercise | undefined) => {
  const routineUsesText = exercise?.usesCount
    ? `Actualmente está siendo usado en ${exercise?.usesCount > 1 ? `${exercise?.usesCount} rutinas. Desaparecerá de todas ellas` : 'una rutina. Desaparecerá de ella'}.\n\n`
    : '';
  return (
    <>
      ¿Estás seguro de que quieres eliminar el ejercicio <b>{exercise?.name}</b>?{'\n\n'}
      {routineUsesText}Seguirá apareciendo en rutinas del historial.
    </>
  );
};

const tableHeaders: TableColumn<Exercise>[] = [
  {
    label: 'Nombre',
    accessor: 'name',
    type: 'string',
    isTitle: true,
  },
  {
    label: 'Usos en rutina',
    accessor: 'usesCount',
    type: 'number',
  },
  {
    label: 'Músculos',
    accessor: 'muscleGroups',
    type: 'dropdown',
    valueFromCallBack: (_, row) => {
      return row.muscleGroups?.map(capitalize) ?? [];
    },
  },
  {
    label: 'Categoría',
    accessor: 'categories',
    type: 'tag',
    textKeyFromCallBack: (_, row) => {
      return row.categories && row.categories.length > 0 ? capitalize(row.categories[0]) : '';
    },
    colorFromCallBack: () => 'primary',
  },
  {
    label: 'Tipo',
    accessor: 'exerciseType',
    type: 'tag',
    colorFromCallBack: (_, { exerciseType }) => {
      return exerciseType === EXERCISE_TYPE.REPS ? 'info' : 'secondary';
    },
    textKeyFromCallBack: (_, { exerciseType }) => {
      return exerciseType === EXERCISE_TYPE.REPS ? 'Repeticiones' : 'Tiempo';
    },
  },
  { label: 'Imagen', accessor: 'hasImage', type: 'boolean', sortable: false },
  { label: 'Vídeo', accessor: 'hasVideo', type: 'boolean', sortable: false },
];

export const Exercises = () => {
  const { get, delete: del } = useClient();
  const { organization } = useOrganizationContext();
  const { features, featuresLoading } = useAuthContext();

  const {
    isEditing,
    setIsEditing,
    panelVisible,
    closePanel,
    selectedResource,
    setSelectedResource,
    openPanel: originalOpenPanel,
    isDeleting,
    setIsDeleting,
  } = useResourcePanel<Exercise>(undefined);

  const openPanel = useCallback(
    async (isEditing: boolean, resourceOrId?: Exercise | string) => {
      if (typeof resourceOrId === 'string') {
        const exercise = await get<Exercise>(`/exercises/${resourceOrId}`);
        originalOpenPanel(isEditing, exercise);
      } else {
        originalOpenPanel(isEditing, resourceOrId);
      }
    },
    [get, originalOpenPanel],
  );

  const userHasCreateFeature = hasFeaturesPermission([ALL_FEATURES.EXERCISES_WRITE], { features, featuresLoading });

  const allowedFeatures: AllowedFeatures = {
    edit: [ALL_FEATURES.EXERCISES_WRITE],
    delete: [ALL_FEATURES.EXERCISES_WRITE],
    create: [ALL_FEATURES.EXERCISES_WRITE],
  };

  const handleDelete = useCallback(
    async (id: string | undefined) => {
      if (!id || !organization?.id) return false;
      setIsDeleting(true);

      const response = await del<Exercise>(`/exercises/${id}`, {
        errorMessages: {
          summary: 'No se pudo eliminar el ejercicio',
          defaultDetail: 'Hubo un error al intentar borrar el ejercicio',
        },
        successMessage: {
          summary: 'Ejercicio eliminado',
          detail: 'El ejercicio se ha eliminado correctamente',
        },
      });

      setIsDeleting(false);
      if (response) {
        const tableKey = `/organizations/${organization.id}/exercises/paginated?`;
        mutate((key) => typeof key === 'string' && key.startsWith(tableKey));
      }
      return !!response;
    },
    [organization, del, setIsDeleting],
  );

  const options: TableAction[] = useMemo(
    () => [
      {
        label: 'Ver',
        onClick: (id) => {
          openPanel(false, id);
        },
        hasPermission: hasFeaturesPermission([ALL_FEATURES.EXERCISES], { features, featuresLoading }),
      },
      {
        label: 'Editar',
        onClick: (id) => {
          openPanel(true, id);
        },
        hasPermission: userHasCreateFeature,
      },
      {
        label: 'Eliminar',
        onClick: async (id) => {
          const exerciseToDelete = selectedResource?.id === id ? selectedResource : await get<Exercise>(`/exercises/${id}`);
          showDeleteDialog(() => handleDelete(id), {
            header: 'Eliminar ejercicio',
            message: getExerciseDeleteConfirmationMessage(exerciseToDelete),
          });
        },
        hasPermission: userHasCreateFeature,
      },
    ],
    [openPanel, features, featuresLoading, userHasCreateFeature, handleDelete, selectedResource, get],
  );

  const renderHeaderOptions = useCallback(
    (fetchAllForExport: () => Promise<Exercise[]>) => (
      <div className='exercises-header-options'>
        {userHasCreateFeature && (
          <Button
            label='Añadir ejercicio'
            outlined
            rounded
            icon={PrimeIcons.PLUS}
            onClick={() => {
              openPanel(false);
            }}
          />
        )}
        <ExportButton
          fetchData={fetchAllForExport}
          columns={tableHeaders}
          fileName='Ejercicios'
          disabled={isDeleting}
          additionalColumns={[
            {
              label: 'Descripción',
              accessor: 'description',
              type: 'string',
            },
          ]}
        />
      </div>
    ),
    [userHasCreateFeature, openPanel, isDeleting],
  );

  return (
    <PageTemplate className='employees-page' title='Ejercicios'>
      <ServerResponsiveTable
        tableId='exercises'
        endpoint={organization ? `/organizations/${organization.id}/exercises/paginated` : ''}
        disabled={isDeleting}
        columns={tableHeaders}
        options={options}
        headerOptions={renderHeaderOptions}
        onRowClick={(row) => {
          openPanel(false, row.id);
        }}
      />
      <ExercisePanel
        resource={selectedResource}
        visible={panelVisible}
        onHide={closePanel}
        onIsEditingChange={setIsEditing}
        onDeleteResource={handleDelete}
        isDeleting={isDeleting}
        isEditing={isEditing}
        allowedFeatures={allowedFeatures}
      />
    </PageTemplate>
  );
};
